import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import { articles } from "@/mocks/articles";
import { categories } from "@/mocks/meta";
import { ArticleCard } from "@/components/feature/ArticleCards";

export default function CategoryPage() {
  const { slug = "" } = useParams();
  const cat = categories.find((c) => c.slug === slug);
  const [sort, setSort] = useState<"new" | "hot">("new");

  const list = useMemo(() => {
    if (!cat) return [];
    const base = articles.filter((a) => a.category === cat.slug);
    if (sort === "hot") return [...base].sort((x, y) => Number(y.views.replace("万", "")) - Number(x.views.replace("万", "")));
    return base;
  }, [cat, sort]);

  useSeo(
    cat ? `吃瓜精选·${cat.name}盘点｜${cat.brief}` : "栏目不存在",
    cat ? `${cat.name}盘点：${cat.desc}吃瓜精选持续更新与分类整理。` : "该栏目不存在。"
  );

  if (!cat) {
    return (
      <div className="wrap flex min-h-[60vh] flex-col items-center justify-center pt-16 text-center">
        <i className="ri-emotion-sad-line text-5xl text-secondary-400" />
        <h1 className="serif mt-4 text-2xl font-bold text-foreground-950">找不到这个栏目</h1>
        <Link to="/" className="mt-5 rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600">返回首页</Link>
      </div>
    );
  }

  const others = categories.filter((c) => c.slug !== cat.slug);

  return (
    <div className="pt-16 md:pt-[72px]">
      {/* banner */}
      <section className="relative overflow-hidden bg-foreground-900">
        <div className="absolute inset-0">
          <img src={cat.cover} alt={`${cat.name}栏目背景`} className="h-full w-full object-cover object-center opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/70 to-foreground-900/30" />
        </div>
        <div className="wrap relative py-12 md:py-16">
          <nav className="flex flex-wrap items-center gap-1.5 text-sm text-background-300" aria-label="面包屑">
            <Link to="/" className="hover:text-accent-300">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-background-100">栏目盘点</span>
            <i className="ri-arrow-right-s-line" />
            <span className="text-accent-300">{cat.name}</span>
          </nav>
          <p className="eyebrow mt-8 text-accent-300"><span className="eyebrow-line !bg-accent-400" />{cat.nameEn}</p>
          <h1 className="serif mt-3 text-3xl font-black leading-tight text-background-50 md:text-5xl">{cat.name}盘点</h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-background-200 md:text-base">{cat.desc}</p>
          <p className="mt-5 inline-flex items-center gap-2 text-sm text-background-300">
            <i className="ri-file-list-3-line" /> 已收录 {list.length} 篇盘点 · 持续更新
          </p>
        </div>
      </section>

      <section className="wrap py-10 md:py-14">
        {/* sort */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="inline-flex items-center gap-2 rounded-full border border-background-200 bg-background-50 p-1">
            {[
              { k: "new" as const, label: "最新收录", icon: "ri-time-line" },
              { k: "hot" as const, label: "按热度", icon: "ri-fire-line" },
            ].map((s) => (
              <button
                key={s.k}
                type="button"
                onClick={() => setSort(s.k)}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                  sort === s.k ? "bg-foreground-950 text-background-50" : "text-foreground-700 hover:text-primary-700"
                }`}
              >
                <i className={s.icon} /> {s.label}
              </button>
            ))}
          </div>
          <p className="text-sm text-secondary-600">相关专题：请移步首页「专题合集」查看跨栏目完整脉络</p>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 md:gap-5">
          {list.map((a) => (
            <ArticleCard key={a.slug} a={a} />
          ))}
        </div>
      </section>

      {/* other categories */}
      <section className="border-t border-background-200/70 bg-background-100/60 py-10 md:py-12">
        <div className="wrap">
          <h2 className="serif text-xl font-bold text-foreground-950">继续逛别的栏目</h2>
          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {others.map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className="group flex items-center gap-3 rounded-xl border border-background-200/70 bg-background-50 p-4 transition-colors hover:border-primary-300/70"
              >
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-primary-700">
                  <i className={`${c.slug === "starlove" ? "ri-heart-3-line" : c.slug === "backstage" ? "ri-mic-line" : c.slug === "variety" ? "ri-tv-2-line" : "ri-chat-3-line"} text-lg`} />
                </span>
                <span className="min-w-0">
                  <span className="block text-sm font-bold text-foreground-950">{c.name}</span>
                  <span className="block truncate text-xs text-secondary-600">{c.brief}</span>
                </span>
                <i className="ri-arrow-right-line ml-auto text-secondary-400 transition-colors group-hover:text-primary-600" />
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}